/**
 * A deliberately tiny key-lookup module.
 *
 * Every user-facing string in this app lives in `src/i18n/<locale>/`, from the
 * first iteration onward, and a build gate fails when one is inlined in a
 * component (architecture §4). That is not tidiness: spec §15 requires the copy
 * to be reviewable *as a set* against the tone rules — no guilt, observational
 * insight wording, Tier-1/Tier-2 claims discipline — and strings scattered
 * across a dozen screens have to be excavated before they can be reviewed.
 *
 * Spoken-aloud strings are reviewed for speakability, not just readability
 * (spec §7), which is another thing only possible when they sit together.
 */

import { en } from './en/index';

export const LOCALES = ['en', 'de'] as const;
export type Locale = (typeof LOCALES)[number];

export type Messages = typeof en;
export type MessageKey = keyof Messages;

const catalogs: Record<Locale, Partial<Messages>> = {
  en,
  // German lands in I5; the mechanism exists from the start so nothing has to
  // be excavated later.
  de: {},
};

let active: Locale = 'en';

export function setLocale(locale: Locale): void {
  active = locale;
}

export function getLocale(): Locale {
  return active;
}

/**
 * Look up a string, optionally interpolating `{name}` placeholders.
 *
 * A missing key returns the key itself rather than an empty string: silence in
 * this product is a deliberate design choice (the intention line prefers it to
 * filler), so a blank must never be something a bug can produce.
 */
export function t(key: MessageKey, vars?: Record<string, string | number>): string {
  const template = catalogs[active][key] ?? catalogs.en[key] ?? key;
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    Object.hasOwn(vars, name) ? String(vars[name]) : whole,
  );
}
